import { getCenter, switchCursor } from '../../configurator/helpers';
import { Coordinate } from '../../configurator/model/coordinate';
import NumberFormatter from '../../utils/number-formatter';
import { ActiveToolMode } from './active-tool-mode';
import { renderHorizontalHelperMeasurements, renderVerticalHelperMeasurements } from './heights-measurements';
import { SnapToMode } from './snap-to-mode';

export default class HeightsAreaRenderer {
  outlineColor = '#b9b9b9';
  outlineWidth = 1;
  outlinePointColor = 'black';
  outlinePointColorHighlighted = 'red';
  pointFillHighlightedColor = 'red';
  pointFillColor = 'white';
  outlinePointSize = 7;
  slopeArrowWidth = 3;
  slopeArrowColor = 'red';
  slopeArrowSize = 120;
  fillColor = '#d0d0d0';
  lastGlobalMousePosition;
  labelFontSize = 12;
  labelColor = 'white';
  labelBackground = 'black';
  labelPadding = 2;
  cutoutFillColor = 'white';
  snapToEdgeLocalDistance = 15;
  snapToSmartGuideLocalDistance = 15;
  snapToPointLocalDistance = 25;

  _activeToolMode = ActiveToolMode.MIN_HEIGHT_SELECTION;
  _snapToMode = SnapToMode.NONE;

  constructor(options = {}) {
    ({
      group: this.parentGroup,
      areaState: this.areaState,
      view: this.view,
      onPointClicked: this.onPointClicked,
      onPointAdded: this.onPointAdded,
      onSlopeStartSelected: this.onSlopeStartSelected,
      onSlopeConfirmed: this.onSlopeConfirmed,
      onPointRemoved: this.onPointRemoved,
    } = options);
    if (options.highlightColor) {
      this.outlinePointColorHighlighted = options.highlightColor;
      this.pointFillHighlightedColor = options.highlightColor;
      this.slopeArrowColor = options.highlightColor;
    }
    this.numberFormatter = new NumberFormatter({});
    this.formattingPattern = 'feet_inch_fraction';
  }

  render() {
    this.group ? this.group.clear() : (this.group = this.parentGroup.createGroup());
    this.addPointGroup = undefined;
    this.slopeGroup = undefined;
    this.activeAreaZoneGroup = undefined;
    this.pointLabelsGroup = undefined;
    this.measurementGroup = undefined;

    this.renderArea();
    this.renderCutouts();
    this.renderOutline();

    switch (this._activeToolMode) {
      case ActiveToolMode.SLOPE_START_SELECTION:
        this.renderPoints();
        this.renderPointLabels();
        break;
      case ActiveToolMode.SLOPE_SELECTION:
        this.renderPoints();
        this.renderPointLabels();
        this.renderSlope();
        this.renderAreaActiveZone();
        break;
      case ActiveToolMode.ADD_POINT:
        this.renderAddPoint();
        this.renderAreaActiveZone();
        this.renderPoints();
        this.renderPointLabels();
    }
  }

  renderSlope() {
    if (this._activeToolMode !== ActiveToolMode.SLOPE_SELECTION) return;
    this.slopeGroup ? this.slopeGroup.clear() : (this.slopeGroup = this.group.createGroup());
    if (!this.lastGlobalMousePosition) {
      return;
    }
    const localSlopeStart = this.view.convertGlobalToLocal(this.areaState.slopeStartPoint);
    const localSlopeEnd = this.view.convertGlobalToLocal(this.lastGlobalMousePosition);
    const line = this.slopeGroup.createLine({
      x1: localSlopeStart.x,
      y1: localSlopeStart.y,
      x2: localSlopeEnd.x,
      y2: localSlopeEnd.y,
    });
    line.setStroke({ color: this.slopeArrowColor, width: this.slopeArrowWidth });
    const arrow = this.slopeGroup.createPolyline([
      { x: localSlopeEnd.x, y: localSlopeEnd.y },
      {
        x: localSlopeEnd.x - Math.sqrt(this.slopeArrowSize) / Math.sqrt(2),
        y: localSlopeEnd.y - Math.sqrt(this.slopeArrowSize) / Math.sqrt(2),
      },
      {
        x: localSlopeEnd.x + Math.sqrt(this.slopeArrowSize) / Math.sqrt(2),
        y: localSlopeEnd.y - Math.sqrt(this.slopeArrowSize) / Math.sqrt(2),
      },
      { x: localSlopeEnd.x, y: localSlopeEnd.y },
    ]);
    arrow.setFill(this.slopeArrowColor);
    let angle =
      Math.atan((localSlopeStart.y - localSlopeEnd.y) / (localSlopeStart.x - localSlopeEnd.x)) * (180 / Math.PI) - 90;
    if (localSlopeStart.x >= localSlopeEnd.x) {
      angle += 180;
    }
    arrow.setTransform([dojox.gfx.matrix.rotategAt(angle, localSlopeEnd.x, localSlopeEnd.y)]);

    // render slope value
    const center = getCenter(localSlopeStart, localSlopeEnd);
    const textNode = this.slopeGroup.createText({
      x: center.x,
      y: center.y + this.labelFontSize / 2,
      text: `${this.areaState.slopeUserSelection.value} ${
        this.areaState.slopeUserSelection.mode === 'angle' ? '°' : '%'
      }`,
      align: 'middle',
    });
    textNode.setFill('white').setFont({ family: 'sans-serif', size: this.labelFontSize + 'px' });
    // render slope value background
    const bboxOriginal = textNode.rawNode.getBBox();
    const bbox = {
      x: bboxOriginal.x - this.labelPadding,
      y: bboxOriginal.y - this.labelPadding,
      width: bboxOriginal.width + 2 * this.labelPadding,
      height: bboxOriginal.height + 2 * this.labelPadding,
    };
    const backgroundNode = this.slopeGroup.createRect(bbox);
    backgroundNode.setFill(this.slopeArrowColor);
    textNode.moveToFront();
  }

  renderAddPoint() {
    this.addPointGroup ? this.addPointGroup.clear() : (this.addPointGroup = this.group.createGroup());
    if (!this.lastGlobalMousePosition) {
      return;
    }
    switchCursor(this.group, 'crosshair');
    const localCoords = this.view.convertGlobalToLocal(this.lastGlobalMousePosition);
    const circle = this.addPointGroup.createCircle({
      cx: localCoords.x,
      cy: localCoords.y,
      r: this.outlinePointSize,
    });
    circle.setFill(this.pointFillHighlightedColor);
    circle.setStroke({ color: this.pointFillHighlightedColor, width: this.outlineWidth });
  }

  renderPoints() {
    const pointsGroup = this.group.createGroup();

    for (const point of this.areaState.heightPoints) {
      const localCoords = this.view.convertGlobalToLocal(point);
      const circleGroup = pointsGroup.createGroup();
      const circle = circleGroup.createCircle({
        cx: localCoords.x,
        cy: localCoords.y,
        r: this.outlinePointSize,
      });
      switchCursor(circleGroup, 'pointer');
      circle.setStroke({
        color: point.highlighted ? this.outlinePointColorHighlighted : this.outlinePointColor,
        width: this.outlineWidth,
      });
      circle.setFill(point.highlighted ? this.pointFillHighlightedColor : this.pointFillColor);
      circleGroup.connect('mouseover', e => {
        e.stopPropagation();
      });
      circleGroup.connect('mouseout', e => {
        e.stopPropagation();
      });
      circleGroup.connect('click', e => {
        e.stopPropagation();
        this.pointClicked(point, e);
      });
    }
  }

  renderPointLabels() {
    this.pointLabelsGroup ? this.pointLabelsGroup.clear() : (this.pointLabelsGroup = this.group.createGroup());
    for (const point of this.areaState.heightPoints) {
      if (!point.finished) {
        continue;
      }
      const singleLabelGroup = this.pointLabelsGroup.createGroup();
      const removeNodeWidth = point.isInitial ? 0 : 20;
      const center = this.view.convertGlobalToLocal(point);
      const labelPadding = this.labelPadding;
      const heightMm = point.highlighted
        ? point.height
        : this._activeToolMode === ActiveToolMode.SLOPE_SELECTION && this.areaState.slopeStartPoint
        ? point.tempHeight
        : point.height;
      const text = `${this.numberFormatter.formatOutput(heightMm, this.formattingPattern)}`;

      const textNode = singleLabelGroup.createText({
        x: center.x,
        y: center.y - this.outlinePointSize * 2,
        text: text,
        align: 'right',
      });
      textNode.setFill(this.labelColor).setFont({ family: 'sans-serif', size: this.labelFontSize + 'px' });
      const bboxOriginal = textNode.rawNode.getBBox();
      const bbox = {
        x: bboxOriginal.x - labelPadding,
        y: bboxOriginal.y - labelPadding,
        width: bboxOriginal.width + 2 * labelPadding + removeNodeWidth,
        height: bboxOriginal.height + 2 * labelPadding,
      };
      const backgroundNode = singleLabelGroup.createRect(bbox);
      backgroundNode.setFill(this.labelBackground);
      singleLabelGroup.connect('click', e => {
        e.stopPropagation();
        this.pointClicked(point, e);
      });
      singleLabelGroup.connect('mouseover', e => {
        e.stopPropagation();
        singleLabelGroup.moveToFront();
      });
      switchCursor(singleLabelGroup, 'pointer');

      textNode.moveToFront();
      if (!point.isInitial) {
        const removeWrap = singleLabelGroup.createGroup();
        const removeNode = removeWrap.createRect({
          x: bbox.x + bbox.width - removeNodeWidth,
          y: bbox.y,
          width: removeNodeWidth,
          height: bbox.height,
        });
        removeNode.moveToFront();
        removeNode.setFill(this.labelBackground);
        removeWrap.connect('click', e => {
          e.stopPropagation();
          this.removePoint(point);
        });
        removeWrap
          .createText({
            x: center.x + bbox.width - removeNodeWidth / 2,
            y: center.y - this.outlinePointSize * 2,
            text: '×',
            align: 'center',
          })
          .setFill(this.labelColor)
          .setFont({ family: 'sans-serif', size: this.labelFontSize + 'px' });
      }
    }
  }

  renderArea() {
    const area = this.group.createPolyline(this.view.convertGlobalToLocalAll(this.areaState.shapePoints));
    switchCursor(this.group, 'default');
    area.setFill(this.fillColor);
  }

  renderCutouts() {
    for (const cutout of this.areaState.cutouts) {
      const cutoutPoly = this.group.createPolyline(this.view.convertGlobalToLocalAll(cutout.points));
      cutoutPoly.setFill(this.cutoutFillColor);
    }
  }

  renderAreaActiveZone() {
    this.activeAreaZoneGroup ? this.activeAreaZoneGroup.clear() : (this.activeAreaZoneGroup = this.group.createGroup());
    const area = this.activeAreaZoneGroup.createPolyline(this.view.convertGlobalToLocalAll(this.areaState.shapePoints));
    area.setFill('transparent');
    area.connect('mousemove', e => {
      // e.stopPropagation();
      const currentPositionGlobal = this.view.convertPageToGlobal(new Coordinate(e.pageX, e.pageY));
      const snapToEdgeGlobaDistance = this.view.convertLocalToGlobalDistance(this.snapToEdgeLocalDistance);
      const snapToPointGlobaDistance = this.view.convertLocalToGlobalDistance(this.snapToPointLocalDistance);
      const snapToSmartGuideGlobalDistance = this.view.convertLocalToGlobalDistance(this.snapToSmartGuideLocalDistance);
      switch (this._snapToMode) {
        case SnapToMode.EDGE:
          const snapPointEdge = this.areaState.getSnapEdgePoint(currentPositionGlobal, snapToEdgeGlobaDistance);
          this.lastGlobalMousePosition = snapPointEdge
            ? new Coordinate(snapPointEdge.x, snapPointEdge.y)
            : currentPositionGlobal;
          break;
        case SnapToMode.POINTANDEDGE:
          const snapInitialPoint = this.areaState.getSnapInitialPoint(currentPositionGlobal, snapToPointGlobaDistance);
          if (snapInitialPoint) {
            this.lastGlobalMousePosition = snapInitialPoint;
            break;
          }
          const snapEdgePoint = this.areaState.getSnapEdgePoint(currentPositionGlobal, snapToEdgeGlobaDistance);
          this.lastGlobalMousePosition = snapEdgePoint
            ? new Coordinate(snapEdgePoint.x, snapEdgePoint.y)
            : currentPositionGlobal;
          break;
        case SnapToMode.SMARTGUIDES:
          const snapPointSmart = this.areaState.getSnapSmartGuide(
            currentPositionGlobal,
            snapToSmartGuideGlobalDistance
          );
          this.lastGlobalMousePosition = snapPointSmart
            ? new Coordinate(snapPointSmart.x, snapPointSmart.y)
            : currentPositionGlobal;
          break;
        case SnapToMode.NONE:
        default:
          this.lastGlobalMousePosition = currentPositionGlobal;
          break;
      }
      this.onMouseMove();
    });
    area.connect('mouseup', e => {
      e.stopPropagation();
      this.onMouseUp();
    });
    area.connect('mouseout', e => {
      e.stopPropagation();
      this.onMouseOut();
    });
  }

  renderOutline() {
    this.group
      .createPolyline(this.view.convertGlobalToLocalAll(this.areaState.shapePoints))
      .setStroke({ color: this.outlineColor, width: this.outlineWidth });
    this.group.moveToFront();
  }

  onMouseMove() {
    switch (this._activeToolMode) {
      case ActiveToolMode.ADD_POINT:
        this.renderAddPoint();
        this.renderMeasurements();
        break;
      case ActiveToolMode.SLOPE_SELECTION:
        this.areaState.calculateTempHeights(this.lastGlobalMousePosition);
        this.renderSlope();
        this.renderPointLabels();
        break;
    }
  }

  onMouseUp() {
    switch (this._activeToolMode) {
      case ActiveToolMode.ADD_POINT:
        this.addPoint();
        break;
      case ActiveToolMode.SLOPE_SELECTION:
        this.confirmSlope();
        break;
    }
  }

  onMouseOut() {
    switch (this._activeToolMode) {
      case ActiveToolMode.ADD_POINT:
        this.lastGlobalMousePosition = undefined;
        this.renderAddPoint();
        this.renderMeasurements();
        break;
      case ActiveToolMode.SLOPE_START_SELECTION:
      case ActiveToolMode.SLOPE_SELECTION:
        this.lastGlobalMousePosition = undefined;
        this.renderSlope();
        break;
    }
  }

  pointClicked(point, event) {
    switch (this._activeToolMode) {
      case ActiveToolMode.SLOPE_SELECTION:
      case ActiveToolMode.SLOPE_START_SELECTION:
        this.areaState.slopeStartPoint = point;
        if (!point.highlighted) {
          if (window.confirm(tp_trans.trans('slope_start_not_on_min_height', 'slope_start_not_on_min_height'))) {
            point.height = point.initialMinHeight;
          } else {
            break;
          }
        }
        this.onSlopeStartSelected(point, this.view.convertPageToLocal(new Coordinate(event.pageX, event.pageY)));
        break;
      default:
        this.onPointClicked(point, this.view.convertPageToLocal(new Coordinate(event.pageX, event.pageY)));
    }
  }

  addPoint() {
    if (!this.lastGlobalMousePosition) return;

    const point = this.areaState.addPoint(this.lastGlobalMousePosition);
    this.render();
    this.onPointAdded(point, this.view.convertGlobalToLocal(this.lastGlobalMousePosition));
  }

  removePoint(point) {
    this.areaState.removePoint(point);
    this.render();
    this.onPointRemoved();
  }

  confirmSlope() {
    if (!this.lastGlobalMousePosition) return;
    this.onSlopeConfirmed(this.areaState);
  }

  renderMeasurements() {
    this.measurementGroup ? this.measurementGroup.clear() : (this.measurementGroup = this.group.createGroup());
    if (this._activeToolMode === ActiveToolMode.ADD_POINT && this.lastGlobalMousePosition) {
      renderVerticalHelperMeasurements(
        this.areaState.shapePoints,
        this.lastGlobalMousePosition,
        this.measurementGroup,
        this.view
      );
      renderHorizontalHelperMeasurements(
        this.areaState.shapePoints,
        this.lastGlobalMousePosition,
        this.measurementGroup,
        this.view
      );
    }
  }

  set activeToolMode(mode) {
    this._activeToolMode = mode;
    switch (this._activeToolMode) {
      case ActiveToolMode.ADD_POINT:
        this._snapToMode = SnapToMode.SMARTGUIDES;
        this.lastGlobalMousePosition = undefined;
        break;
      case ActiveToolMode.SLOPE_SELECTION:
        this._snapToMode = SnapToMode.POINTANDEDGE;
        this.lastGlobalMousePosition = this.areaState.center;
        break;
      default:
        this._snapToMode = SnapToMode.NONE;
        this.lastGlobalMousePosition = undefined;
    }
    this.render();
  }
}
